import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Send, Search, Filter } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { calendarAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import NotificationComposeDialog from './NotificationComposeDialog';

const STATUS_BADGE = {
  scheduled: 'bg-slate-100 text-slate-700 border-slate-200',
  processing: 'bg-sky-100 text-sky-700 border-sky-200',
  sent: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  failed: 'bg-rose-100 text-rose-700 border-rose-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
  skipped: 'bg-slate-100 text-slate-500 border-slate-200',
};

const TRIGGER_LABEL = {
  registration_confirmed: 'Registration confirmed',
  registration_cancelled: 'Registration cancelled',
  waitlist_joined: 'Waitlist joined',
  waitlist_promoted: 'Waitlist promoted',
  event_cancelled: 'Event cancelled',
  event_updated: 'Event updated',
  event_published: 'Event published',
  reminder_24h: 'Reminder — 24h',
  reminder_1h: 'Reminder — 1h',
  custom: 'Custom broadcast',
};

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

/**
 * Phase 27.30.12 — Notifications panel for the Event Workspace.
 *
 * Combines the manager view of every email that has gone out for the
 * event (with filters + resend) plus a composer to broadcast a custom
 * message to a filtered slice of the participant roster.
 */
export default function NotificationsPanel({ event, canManage }) {
  const eventId = event?.event_id;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);

  const [triggerFilter, setTriggerFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [recipientQ, setRecipientQ] = useState('');

  const load = useCallback(async () => {
    if (!eventId || !canManage) return;
    setLoading(true);
    try {
      const params = { page_size: 200 };
      if (triggerFilter !== 'all') params.trigger_type = triggerFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (recipientQ.trim()) params.recipient = recipientQ.trim();
      const { data } = await calendarAPI.notificationsHistory(eventId, params);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load notifications'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [eventId, canManage, triggerFilter, statusFilter, recipientQ]);

  useEffect(() => { load(); }, [load]);

  const availableTriggers = useMemo(() => {
    const set = new Set(items.map((i) => i.trigger_type).filter(Boolean));
    return Array.from(set);
  }, [items]);

  if (!canManage) {
    return (
      <Card>
        <CardContent className="p-5 text-sm text-slate-500" data-testid="notifications-panel-restricted">
          Notification history is available to event managers only.
        </CardContent>
      </Card>
    );
  }

  const doResend = async (id) => {
    if (!window.confirm('Resend this notification to the recipient?')) return;
    setResending(id);
    try {
      await calendarAPI.notificationResend(eventId, id);
      toast.success('Resend queued');
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Resend failed'));
    } finally {
      setResending(null);
    }
  };

  return (
    <Card data-testid="notifications-panel">
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-800">Notification history</div>
            <div className="text-xs text-slate-500">
              {total} email{total === 1 ? '' : 's'} on record for this event
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={load}
              disabled={loading}
              data-testid="notifications-refresh-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            {canManage && (
              <Button
                size="sm"
                onClick={() => setComposeOpen(true)}
                data-testid="notifications-compose-btn"
              >
                <Send className="h-3.5 w-3.5 mr-1" /> Compose email
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="notifications-filters">
          <div className="flex items-center gap-1.5">
            <Filter className="h-3.5 w-3.5 text-slate-400" />
            <Select value={triggerFilter} onValueChange={setTriggerFilter}>
              <SelectTrigger className="h-9 text-xs" data-testid="filter-trigger">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {Object.entries(TRIGGER_LABEL).map(([k, label]) => (
                  <SelectItem
                    key={k}
                    value={k}
                    disabled={!availableTriggers.includes(k) && items.length > 0}
                  >
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 text-xs" data-testid="filter-status">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="skipped">Skipped</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={recipientQ}
              onChange={(e) => setRecipientQ(e.target.value)}
              placeholder="Filter by recipient email…"
              className="pl-8 h-9 text-xs"
              data-testid="filter-recipient"
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <p className="text-sm text-slate-500 py-8 text-center">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center" data-testid="notifications-empty">
            No emails match these filters.
          </p>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded" data-testid="notifications-table">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">Recipient</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Subject</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2 w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((n) => (
                  <tr key={n.id} data-testid={`notif-row-${n.id}`} className="hover:bg-slate-50/60">
                    <td className="px-3 py-2 text-slate-800 truncate max-w-[220px]">{n.recipient_email}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {TRIGGER_LABEL[n.trigger_type] || n.trigger_type}
                    </td>
                    <td className="px-3 py-2 text-slate-600 truncate max-w-[280px]" title={n.subject}>
                      {n.subject || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={`text-[10px] border ${STATUS_BADGE[n.status] || ''}`}>{n.status}</Badge>
                      {n.failure_reason && (
                        <div className="text-[10px] text-rose-600 mt-0.5" title={n.failure_reason}>
                          {n.failure_reason}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-[11px]">
                      {fmt(n.sent_at || n.failed_at || n.created_at)}
                    </td>
                    <td className="px-3 py-2">
                      {canManage && (
                        <Button
                          size="sm" variant="outline"
                          className="h-7 text-[11px]"
                          disabled={resending === n.id}
                          onClick={() => doResend(n.id)}
                          data-testid={`notif-resend-${n.id}`}
                        >
                          Resend
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <NotificationComposeDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        event={event}
        onSent={() => { setComposeOpen(false); load(); }}
      />
    </Card>
  );
}
